import React, { useMemo } from 'react'

const GROUP_LABELS = {
  australian: 'Australian',
  international: 'International',
  none: 'No document',
}

const DEFAULT_DOCS = [
  { id: 'au_drivers_licence', group: 'australian', label: "Driver's licence", documentType: 'drivers_licence', dvsEnabled: true, enabled: true, sortOrder: 10 },
  { id: 'au_birth_certificate', group: 'australian', label: 'Birth certificate', documentType: 'birth_certificate', dvsEnabled: false, enabled: true, sortOrder: 20 },
  { id: 'au_passport', group: 'australian', label: 'Passport', documentType: 'passport', dvsEnabled: true, enabled: true, sortOrder: 30 },
  { id: 'au_medicare', group: 'australian', label: 'Medicare', documentType: 'medicare', dvsEnabled: true, enabled: true, sortOrder: 40 },
  { id: 'au_citizenship_certificate', group: 'australian', label: 'Citizenship certificate', documentType: 'citizenship_certificate', dvsEnabled: false, enabled: true, sortOrder: 50 },
  { id: 'au_damaged_document', group: 'australian', label: 'Damaged document', documentType: 'damaged_document', dvsEnabled: false, enabled: true, sortOrder: 60 },
  { id: 'intl_passport', group: 'international', label: 'Passport', documentType: 'intl_passport', dvsEnabled: false, enabled: true, sortOrder: 70 },
  { id: 'intl_visa', group: 'international', label: 'Visa', documentType: 'visa', dvsEnabled: false, enabled: true, sortOrder: 80 },
  { id: 'no_document', group: 'none', label: 'No document', documentType: 'none', dvsEnabled: false, enabled: true, sortOrder: 90 },
]

function normalizeDocs(value) {
  let arr = value
  if (typeof value === 'string') {
    try {
      arr = JSON.parse(value)
    } catch {
      return DEFAULT_DOCS.map((d) => ({ ...d }))
    }
  }
  if (!Array.isArray(arr) || arr.length === 0) return DEFAULT_DOCS.map((d) => ({ ...d }))
  return arr
    .filter((row) => row && typeof row === 'object' && row.id)
    .map((row, idx) => ({
      id: String(row.id),
      group: String(row.group || 'australian'),
      label: String(row.label || row.id),
      documentType: String(row.documentType || row.document_type || row.id),
      dvsEnabled: !!(row.dvsEnabled ?? row.dvs_enabled ?? row.dvsEligible),
      enabled: row.enabled == null ? true : !!row.enabled,
      sortOrder: Number.isFinite(Number(row.sortOrder ?? row.sort_order))
        ? Number(row.sortOrder ?? row.sort_order)
        : (idx + 1) * 10,
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder)
}

function toStorage(docs) {
  return docs.map((d) => ({
    id: d.id,
    group: d.group,
    label: d.label,
    documentType: d.documentType,
    dvsEnabled: !!d.dvsEnabled,
    enabled: d.enabled !== false,
    sortOrder: Number(d.sortOrder) || 0,
  }))
}

function MiniToggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${
        disabled ? 'opacity-40 cursor-not-allowed' : ''
      } ${checked ? 'bg-teal-600' : 'bg-gray-300'}`}
    >
      <span
        className="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition"
        style={{ transform: checked ? 'translateX(18px)' : 'translateX(2px)' }}
      />
    </button>
  )
}

export default function KycDocumentsEditor({ value, onChange }) {
  const docs = useMemo(() => normalizeDocs(value), [value])

  const update = (next) => onChange(toStorage(next))

  const patchRow = (idx, patch) => {
    const next = docs.map((d, i) => (i === idx ? { ...d, ...patch } : d))
    update(next)
  }

  const byGroup = ['australian', 'international', 'none'].map((g) => ({
    group: g,
    rows: docs
      .map((d, idx) => ({ d, idx }))
      .filter(({ d }) => d.group === g),
  }))

  return (
    <div className="py-3">
      <div className="text-sm font-medium text-gray-900">Assisted KYC documents</div>
      <p className="text-xs text-gray-500 mt-0.5 mb-3">
        Controls which document types appear in the mobile picker. <strong>DVS</strong> runs GreenID
        verification for that type; when off, the payout still creates a verification queue entry and
        runs PEP, but skips GBG.
      </p>
      <div className="space-y-4">
        {byGroup.map(({ group, rows }) =>
          rows.length === 0 ? null : (
            <div key={group}>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                {GROUP_LABELS[group] || group}
              </div>
              <div className="border border-gray-200 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs text-gray-500">
                    <tr>
                      <th className="px-3 py-2 font-medium">Document</th>
                      <th className="px-3 py-2 font-medium w-24 text-center">In picker</th>
                      <th className="px-3 py-2 font-medium w-24 text-center">DVS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map(({ d, idx }) => (
                      <tr key={d.id} className={d.enabled === false ? 'bg-gray-50/80' : ''}>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            className="w-full text-sm border border-gray-200 rounded px-2 py-1"
                            value={d.label}
                            onChange={(e) => patchRow(idx, { label: e.target.value })}
                          />
                          <div className="text-[11px] text-gray-400 mt-0.5 font-mono">{d.id}</div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex justify-center">
                            <MiniToggle
                              checked={d.enabled !== false}
                              onChange={(v) => patchRow(idx, { enabled: v })}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex justify-center">
                            <MiniToggle
                              checked={!!d.dvsEnabled}
                              disabled={d.documentType === 'none'}
                              onChange={(v) => patchRow(idx, { dvsEnabled: v })}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  )
}
